import React, { useEffect, useState, useRef, useMemo } from "react";
import { Card } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaList, FaTimes, FaChevronDown, FaFilter, FaCheck, FaDownload } from "react-icons/fa";
import { fetchRecentOpenTickets } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";
import TicketDetailModal, { getPriorityStyle, getStatusStyle } from "./TicketDetailModal";
import FreshserviceTicketModal from "./FreshserviceTicketModal";
import { formatAbsoluteDate } from "../utils/formatDate";

const RecentOpenTicketsTable = ({ className, clientId, clientName }) => {
  const { getCachedData, setCachedData } = useDataCache();

  const [tickets, setTickets]                   = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [isRefetching, setIsRefetching]         = useState(false);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedSources, setSelectedSources]       = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);
  const isMobile = window.innerWidth <= 992;
  const isInitialLoad = useRef(true);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  const openTicketModal  = (ticket) => { setSelectedTicket(ticket); setShowTicketModal(true); };
  const closeTicketModal = () => { setShowTicketModal(false); setSelectedTicket(null); };

  // ── Export CSV ──────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    if (filteredTickets.length === 0) return;
    const headers = ["Ticket ID", "Ticket Title", "Priority", "Status", "Created Date", "Assigned To"];
    const rows    = filteredTickets.map(t => [
      t.ticket_number || t.id,
      `"${(t.title || "").replace(/"/g, '""')}"`,
      t.priority || "",
      t.status || "",
      t.created_date || "",
      `"${(t.owner_name || "").replace(/"/g, '""')}"`
    ]);
    const rawClientName  = clientName || "All_Clients";
    const safeClientName = rawClientName.replace(/[^a-z0-9]/gi, "_");
    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${safeClientName}_tickets_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Filter: source + priority ────────────────────────────────────────────────
  const PRIORITY_OPTIONS = ["Critical", "High", "Medium", "Low", "Unknown"];
  const SOURCE_OPTIONS   = [
    { key: "freshservice", label: "Freshservice" },
    { key: "autotask",     label: "Autotask" },
  ];

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const sourceMatch   = selectedSources.length === 0 || selectedSources.includes(t.source);
      const priorityMatch = selectedPriorities.length === 0 || selectedPriorities.includes(t.priority);
      return sourceMatch && priorityMatch;
    });
  }, [tickets, selectedSources, selectedPriorities]);

  const activeFilterCount = selectedSources.length + selectedPriorities.length;

  const togglePriority = (p) => setSelectedPriorities(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  const toggleSource   = (s) => setSelectedSources(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const clearAllFilters = () => { setSelectedPriorities([]); setSelectedSources([]); };
  const setSingleSourceFilter = (s) => setSelectedSources([s]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target))
        setShowFilterDropdown(false);
    };
    if (showFilterDropdown) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterDropdown]);

  // ── Fetch tickets ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) { setLoading(false); setTickets([]); return; }

    const cacheKey = `recent_open_tickets_v2_100_${clientId}`;
    const cached   = getCachedData(cacheKey, clientId);

    if (cached !== undefined) {
      setTickets(cached.tickets || []);
      setLoading(false);
      setIsRefetching(false);
      isInitialLoad.current = false;
      return;
    }

    if (isInitialLoad.current) setLoading(true);
    else setIsRefetching(true);

    fetchRecentOpenTickets(clientId, 100)
      .then((res) => {
        setTickets(res.tickets || []);
        setCachedData(`recent_open_tickets_v2_100_${clientId}`, res, clientId);
        setLoading(false);
        setIsRefetching(false);
        isInitialLoad.current = false;
      })
      .catch(() => {
        setTickets([]);
        setLoading(false);
        setIsRefetching(false);
        isInitialLoad.current = false;
      });
  }, [clientId, getCachedData, setCachedData]);

  const getTicketNumberStyle = () => ({
    background: "none", color: "#3b82f6", padding: 0, fontSize: 13,
    fontWeight: 600, border: "none", cursor: "pointer", display: "inline-block",
    textDecoration: "none", transition: "all 0.2s ease",
  });

  if (loading) {
    return (
      <Card className="shadow-sm" style={{ borderRadius: 16, background: "var(--color-bg-card)", border: "1px solid var(--color-border)" }}>
        <Card.Body>
          <div style={{ minHeight: "47dvh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <p style={{ color: "var(--color-text-light)" }}>Loading...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const colWidths = ["22%", "8%", "13%", "17%", "22%", "18%"];
  const colHeaders = ["Ticket ID", "Source", "Priority", "Status", "Queue", "Created"];

  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
    </colgroup>
  );

  const tableBase = {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "collapse",
    color: "var(--color-text)",
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={className} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <Card className="shadow-sm" style={{ borderRadius: 16, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
        <Card.Body style={{ paddingTop: 24, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", minHeight: 0 }}>
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4 mt-2" style={{ flexWrap: isMobile ? "wrap" : "nowrap", gap: 16, flexShrink: 0 }}>
            <h6 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: "var(--color-text)" }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(59,130,246,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FaList size={16} color="#3b82f6" />
              </div>
              Open Tickets
            </h6>

            <div className="d-flex align-items-center gap-2">
              {/* Export */}
              {filteredTickets.length > 0 && (
                <div
                  onClick={handleExportCSV}
                  style={{ borderRadius: 12, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", padding: "8px 14px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", transition: "all 0.2s ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(59,130,246,0.5)"; e.currentTarget.style.background = "rgba(59,130,246,0.05)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--color-border)"; e.currentTarget.style.background = "var(--color-bg-card)"; }}
                  title="Export to CSV"
                >
                  <FaDownload size={11} color="#3b82f6" />
                  <span style={{ color: "var(--color-text)", fontWeight: 500 }}>Export</span>
                </div>
              )}

              {/* Unified filter */}
              <div className="position-relative" ref={filterDropdownRef}>
                <div
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  style={{ borderRadius: 12, background: activeFilterCount > 0 ? "#ffffff" : "var(--color-bg-card)", border: activeFilterCount > 0 ? "1px solid rgba(245,158,11,0.3)" : "1px solid var(--color-border)", padding: "8px 14px", fontSize: 13, cursor: "pointer", minWidth: isMobile ? 110 : 130, display: "flex", alignItems: "center", gap: 8, boxShadow: "0 2px 4px rgba(0,0,0,0.15)", transition: "all 0.2s ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(245,158,11,0.5)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = activeFilterCount > 0 ? "rgba(245,158,11,0.3)" : "var(--color-border)"; }}
                >
                  <FaFilter size={11} color="#f59e0b" />
                  <span style={{ color: activeFilterCount > 0 ? "#f59e0b" : "var(--color-text)", fontWeight: 500, flex: 1 }}>
                    {activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter"}
                  </span>
                  {activeFilterCount > 0 ? (
                    <FaTimes size={10} color="#f59e0b" onClick={(e) => { e.stopPropagation(); clearAllFilters(); }} style={{ cursor: "pointer" }} />
                  ) : (
                    <FaChevronDown size={10} style={{ color: "var(--color-text-light)", transform: showFilterDropdown ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
                  )}
                </div>

                <AnimatePresence>
                  {showFilterDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "var(--color-bg-card)", borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,0.2)", border: "1px solid var(--color-border)", zIndex: 1000, minWidth: 200, padding: 8 }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px 8px", borderBottom: "1px solid var(--color-border)", marginBottom: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text)" }}>Filters</span>
                        {activeFilterCount > 0 && (
                          <button onClick={clearAllFilters} style={{ background: "none", border: "none", color: "#ef4444", fontSize: 10, fontWeight: 500, cursor: "pointer", padding: 0 }}>Clear all</button>
                        )}
                      </div>

                      {/* Source section */}
                      <div style={{ padding: "2px 10px 6px", fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--color-text-light)" }}>Source</div>
                      <div
                        onClick={() => setSelectedSources([])}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: selectedSources.length === 0 ? "rgba(245,158,11,0.1)" : "transparent" }}
                        onMouseEnter={(e) => { if (selectedSources.length !== 0) e.currentTarget.style.background = "rgba(245,158,11,0.05)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = selectedSources.length === 0 ? "rgba(245,158,11,0.1)" : "transparent"; }}
                      >
                        <div style={{ width: 16, height: 16, borderRadius: 4, border: selectedSources.length === 0 ? "none" : "1.5px solid var(--color-border)", background: selectedSources.length === 0 ? "#f59e0b" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {selectedSources.length === 0 && <FaCheck size={9} color="#fff" />}
                        </div>
                        <span style={{ fontSize: 12, color: "var(--color-text)", fontWeight: selectedSources.length === 0 ? 600 : 400 }}>All</span>
                      </div>
                      {SOURCE_OPTIONS.map(({ key, label }) => (
                        <div key={key} onClick={() => toggleSource(key)}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: selectedSources.includes(key) ? "rgba(245,158,11,0.1)" : "transparent" }}
                          onMouseEnter={(e) => { if (!selectedSources.includes(key)) e.currentTarget.style.background = "rgba(245,158,11,0.05)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = selectedSources.includes(key) ? "rgba(245,158,11,0.1)" : "transparent"; }}
                        >
                          <div style={{ width: 16, height: 16, borderRadius: 4, border: selectedSources.includes(key) ? "none" : "1.5px solid var(--color-border)", background: selectedSources.includes(key) ? "#f59e0b" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {selectedSources.includes(key) && <FaCheck size={9} color="#fff" />}
                          </div>
                          <span style={{ fontSize: 12, color: "var(--color-text)", fontWeight: selectedSources.includes(key) ? 600 : 400 }}>{label}</span>
                        </div>
                      ))}

                      <div style={{ borderTop: "1px solid var(--color-border)", margin: "8px 0" }} />

                      {/* Priority section */}
                      <div style={{ padding: "2px 10px 6px", fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--color-text-light)" }}>Priority</div>
                      {PRIORITY_OPTIONS.map((priority) => (
                        <div key={priority} onClick={() => togglePriority(priority)}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: selectedPriorities.includes(priority) ? "rgba(245,158,11,0.1)" : "transparent" }}
                          onMouseEnter={(e) => { if (!selectedPriorities.includes(priority)) e.currentTarget.style.background = "rgba(245,158,11,0.05)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = selectedPriorities.includes(priority) ? "rgba(245,158,11,0.1)" : "transparent"; }}
                        >
                          <div style={{ width: 16, height: 16, borderRadius: 4, border: selectedPriorities.includes(priority) ? "none" : "1.5px solid var(--color-border)", background: selectedPriorities.includes(priority) ? "#f59e0b" : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {selectedPriorities.includes(priority) && <FaCheck size={9} color="#fff" />}
                          </div>
                          <span style={{ fontSize: 12, color: "var(--color-text)", fontWeight: selectedPriorities.includes(priority) ? 600 : 400 }}>{priority}</span>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Active filter tags */}
          {(selectedSources.length > 0 || selectedPriorities.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, flexShrink: 0 }}>
              {selectedSources.map((s) => (
                <span key={`src-${s}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "#f59e0b", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: "4px 10px" }}>
                  Source: {SOURCE_OPTIONS.find(o => o.key === s)?.label || s}
                  <FaTimes size={9} style={{ cursor: "pointer" }} onClick={() => setSelectedSources(prev => prev.filter(x => x !== s))} />
                </span>
              ))}
              {selectedPriorities.map((p) => (
                <span key={`pri-${p}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: "#f59e0b", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 20, padding: "4px 10px" }}>
                  Priority: {p}
                  <FaTimes size={9} style={{ cursor: "pointer" }} onClick={() => setSelectedPriorities(prev => prev.filter(x => x !== p))} />
                </span>
              ))}
            </div>
          )}

          {/* Pinned column headers — outside the scroll container */}
          {filteredTickets.length > 0 && (
            <table style={{ ...tableBase, flexShrink: 0 }}>
              <ColGroup />
              <thead>
                <tr>
                  {colHeaders.map(h => (
                    <th key={h} style={{
                      background: "var(--color-bg-card)",
                      color: "var(--color-text-light)",
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: "0.03em",
                      borderBottom: "1px solid var(--color-border)",
                      padding: "8px 12px",
                      whiteSpace: "nowrap",
                      opacity: 0.7,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
            </table>
          )}

          {/* Scrollable body — only rows scroll */}
          <div style={{ overflowY: "auto", overflowX: "auto", flex: 1, minHeight: 0, position: "relative", maxHeight: "47dvh", WebkitOverflowScrolling: "touch" }}>
            <AnimatePresence>
              {isRefetching && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
                  style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ background: "var(--color-bg-card)", borderRadius: 12, padding: "16px 24px", boxShadow: "0 8px 32px rgba(0,0,0,0.25)", border: "1px solid var(--color-border)", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(139,92,246,0.3)", borderTopColor: "#8b5cf6", animation: "spin 0.8s linear infinite" }} />
                    <span style={{ color: "var(--color-text)", fontSize: 13, fontWeight: 500 }}>Loading...</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {filteredTickets.length === 0 && !isRefetching ? (
              <div style={{ minHeight: "47dvh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <p style={{ color: "var(--color-text-light)" }}>
                  {!clientId ? "Select a client to view recent open tickets" : activeFilterCount > 0 ? "No tickets match the selected filters" : "No open tickets found"}
                </p>
              </div>
            ) : (
              <table style={tableBase}>
                <ColGroup />
                <tbody>
                  {filteredTickets.map((ticket, index) => (
                    <tr key={index}
                      style={{ borderBottom: "1px solid var(--color-border)", transition: "background-color 0.2s ease", backgroundColor: "transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = document.body.classList.contains("dark-mode") ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                    >
                      <td style={{ padding: 12, verticalAlign: "middle", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <button
                          style={getTicketNumberStyle()}
                          onClick={() => openTicketModal(ticket)}
                          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; e.currentTarget.style.color = "#2563eb"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; e.currentTarget.style.color = "#3b82f6"; }}
                        >
                          {ticket.ticket_number || "N/A"}
                        </button>
                      </td>
                      <td style={{ padding: 12, verticalAlign: "middle", whiteSpace: "nowrap" }}>
                        <span
                          onClick={() => setSingleSourceFilter(ticket.source)}
                          title={`Filter by ${ticket.source === "freshservice" ? "Freshservice" : "Autotask"}`}
                          style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: 0.5, cursor: "pointer", transition: "opacity 0.15s ease",
                            ...(ticket.source === "freshservice"
                              ? { background: "rgba(99,102,241,0.15)", color: "#6366f1" }
                              : { background: "rgba(16,185,129,0.15)", color: "#10b981" })
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.opacity = 0.7; }}
                          onMouseLeave={(e) => { e.currentTarget.style.opacity = 1; }}
                        >
                          {ticket.source === "freshservice" ? "FS" : "AT"}
                        </span>
                      </td>
                      <td style={{ padding: 12, verticalAlign: "middle", whiteSpace: "nowrap" }}>
                        <span style={{ ...getPriorityStyle(ticket.priority), padding: "4px 12px", borderRadius: 12 }}>{ticket.priority || "Unknown"}</span>
                      </td>
                      <td style={{ padding: 12, verticalAlign: "middle", whiteSpace: "nowrap" }}>
                        <span style={{ ...getStatusStyle(ticket.status), padding: "4px 12px", borderRadius: 12 }}>{ticket.status || "Unknown"}</span>
                      </td>
                      <td style={{ padding: 12, verticalAlign: "middle", color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.queue || "Unknown"}</td>
                      <td
                        style={{ padding: 12, verticalAlign: "middle", color: "var(--color-text)", whiteSpace: "nowrap" }}
                        title={formatAbsoluteDate(ticket.created_date) || undefined}
                      >
                        {ticket.created || "Unknown"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Shared modals — AT uses TicketDetailModal, FS uses FreshserviceTicketModal */}
      <AnimatePresence>
        {showTicketModal && selectedTicket && (
          selectedTicket.source === "freshservice"
            ? <FreshserviceTicketModal ticket={selectedTicket} onClose={closeTicketModal} />
            : <TicketDetailModal ticket={selectedTicket} onClose={closeTicketModal} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default RecentOpenTicketsTable;